/**
 * Las corridas del mismo flujo alrededor de la que se esta mirando.
 *
 * Responde "¿y ayer como salio?" sin volver al listado: un punto por corrida,
 * con el color de su estado, y saltos a la anterior y la siguiente. La lista
 * completa, con filtros, esta en Corridas.
 *
 * No se refresca sola: las vecinas cambian cuando empieza otra corrida, no cada
 * dos segundos como el grafo. Se recarga al cambiar de corrida.
 */
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import type { FlowRunNeighbors, FlowRunSummary } from '../api/contracts';
import { useMonitorApi } from '../app/api-context';
import { useAsyncResource } from '../hooks/use-async-resource';
import { flowLabel, runStatusLabel } from '../lib/catalogs';
import { formatDateTime } from '../lib/format';
import '../styles/flow-runs.css';

export function RunTimeline({ correlationId }: { readonly correlationId: string }): ReactElement {
  const api = useMonitorApi();
  const resource = useAsyncResource(() => api.getRunNeighbors(correlationId), [api, correlationId]);
  const neighbors = resource.data;

  // Sin flujo no hay "corridas del mismo flujo" que mostrar: un hilo de plomeria
  // (login, handshake) no se compara con nada. Tampoco se ocupa lugar mientras
  // carga ni se grita si falla: el grafo, que es lo principal, ya avisa.
  if (neighbors === null || neighbors.flow === null || neighbors.current === null) {
    return <></>;
  }
  return <RunStrip neighbors={neighbors} current={neighbors.current} flow={neighbors.flow} />;
}

function RunStrip({
  neighbors,
  current,
  flow,
}: {
  readonly neighbors: FlowRunNeighbors;
  readonly current: FlowRunSummary;
  readonly flow: NonNullable<FlowRunNeighbors['flow']>;
}): ReactElement {
  const previous = neighbors.older[0] ?? null;
  const next = neighbors.newer[0] ?? null;
  // De la mas antigua a la mas reciente, de izquierda a derecha.
  const runs = [...[...neighbors.older].reverse(), current, ...neighbors.newer];

  return (
    <section className="panel" aria-labelledby="run-strip-title">
      <header className="panel__header">
        <h2 id="run-strip-title">Corridas de {flowLabel(flow)}</h2>
        <Link to={`/corridas?flujo=${flow}`}>Ver todas</Link>
      </header>
      <div className="panel__body run-strip">
        <div className="run-strip__nav">
          {previous === null ? (
            <span className="muted">Es la primera corrida</span>
          ) : (
            <>
              <Link to={runPath(previous)}>← Anterior</Link>
              <span className="muted run-strip__when">{formatDateTime(previous.startedAt)}</span>
            </>
          )}
        </div>
        <ol
          className="run-strip__dots"
          aria-label={`Corridas de ${flow}, de la más antigua a la más reciente`}
        >
          {runs.map((run) => {
            const isCurrent = run.correlationId === current.correlationId;
            const description = `${formatDateTime(run.startedAt)} · ${runStatusLabel(run.status)} · ${run.messageCount} mensajes`;
            const className = `run-strip__dot run-strip__dot--${run.status}${isCurrent ? ' run-strip__dot--current' : ''}`;
            return (
              <li key={run.correlationId}>
                {isCurrent ? (
                  <span
                    className={className}
                    title={`Esta corrida: ${description}`}
                    aria-label={`Esta corrida: ${description}`}
                    aria-current="page"
                  />
                ) : (
                  <Link
                    className={className}
                    to={runPath(run)}
                    title={description}
                    aria-label={description}
                  />
                )}
              </li>
            );
          })}
        </ol>
        <div className="run-strip__nav run-strip__nav--next">
          {next === null ? (
            <span className="muted">Es la más reciente</span>
          ) : (
            <>
              <Link to={runPath(next)}>Siguiente →</Link>
              <span className="muted run-strip__when">{formatDateTime(next.startedAt)}</span>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function runPath(run: FlowRunSummary): string {
  return `/flujo/${encodeURIComponent(run.correlationId)}`;
}
